import type { ApiError } from "@lms/shared-types";
import type { Session } from "../auth/auth";
import { isMock, type RuntimeConfig } from "../config";
import { mockApi } from "../mocks/fixtures";

export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly requestId?: string,
  ) {
    super(message);
    this.name = "ApiRequestError";
  }
}

/**
 * The one place a request leaves the app: attaches the ID token, parses
 * the error shape (design.md §15), never a raw fetch from a component.
 * The API is same-origin (/api/*) so no CORS and no cross-site cookie.
 */
export async function fetchJson<T>(config: RuntimeConfig, session: Session, path: string, init?: RequestInit): Promise<T> {
  if (isMock) return mockApi<T>(session, path, init);
  const res = await fetch(`${config.apiBaseUrl}${path}`, {
    ...init,
    headers: { ...(init?.headers ?? {}), authorization: `Bearer ${session.idToken}`, accept: "application/json" },
  });
  if (res.ok) return (await res.json()) as T;
  let body: ApiError | undefined;
  try {
    body = (await res.json()) as ApiError;
  } catch {
    // no JSON body
  }
  throw new ApiRequestError(res.status, body?.error.code ?? "HTTP_ERROR", body?.error.message ?? `Request failed (${res.status})`, body?.error.request_id);
}
